import { useEffect } from "react"

import { useNeoletterUserProfile } from "shared/api/neoletter-user-profile/useNeoletterUserProfile"
import { emailDomain } from "shared/lib/etracker/emailDomain"
import {
  ETRACKER_SEGMENT,
  sendCustomSegment
} from "shared/lib/etracker/sendCustomSegment"
import { useAuth } from "shared/lib/useAuth"

/**
 * Reports the current user to eTracker as custom dimensions.
 *
 * Slots are configured in the eTracker account; see
 * documentation/decisions/0023-etracker-user-segments.md.
 * Deliberately not gated on Cookiebot consent.
 */
export function useEtrackerUserSegments(): void {
  const { userInfo, isAuthenticated, isLoading } = useAuth()
  // Company lives only on the Neoletter profile, not on UserInfo. The query is
  // already made app-wide in useApp(), so this reads from the Apollo cache.
  const { userProfile } = useNeoletterUserProfile()

  const name = userInfo?.name
  const email = userInfo?.email
  const domain = emailDomain(email)
  const company = userProfile?.company

  useEffect(() => {
    // Login state is not known yet — sending "0" now and "1" moments later
    // would report a bogus logged-out visit.
    if (isLoading) return

    sendCustomSegment(ETRACKER_SEGMENT.SIGNED_IN, isAuthenticated ? "1" : "0")

    if (!isAuthenticated) return

    if (name) sendCustomSegment(ETRACKER_SEGMENT.NAME, name)
    if (email) sendCustomSegment(ETRACKER_SEGMENT.EMAIL, email)
    if (domain) sendCustomSegment(ETRACKER_SEGMENT.DOMAIN, domain)
    if (company) sendCustomSegment(ETRACKER_SEGMENT.COMPANY, company)
  }, [isLoading, isAuthenticated, name, email, domain, company])
}
